import { api, queryKeys, type Withdrawal } from '@alpha/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { DataTable } from '../../components/DataTable';
import { PageHeader } from '../../components/PageHeader';
import { useTableQuery } from '../../hooks/useTableQuery';
import { formatDateTime, formatLds, formatUsd, statusTone } from '../../lib/format';
import { Chip } from '../../ui';

const STATUSES = ['pending', 'approved', 'rejected', 'all'] as const;
type Status = (typeof STATUSES)[number];

export const WITHDRAWAL_TABS = [
  { label: 'Pending', to: '/withdrawals/pending' },
  { label: 'Approved', to: '/withdrawals/approved' },
  { label: 'Rejected', to: '/withdrawals/rejected' },
  { label: 'Reports', to: '/withdrawals/reports' },
  { label: 'Auto Withdrawal', to: '/withdrawals/auto' },
  { label: 'LDS1 Purchase', to: '/withdrawals/lds1-purchase' },
];

const col = createColumnHelper<Withdrawal>();

export function WithdrawalList() {
  const { status: raw = 'pending' } = useParams<{ status: Status }>();
  const status = ((STATUSES as readonly string[]).includes(raw) ? raw : 'pending') as Status;
  const { query, setPage } = useTableQuery();
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: queryKeys.withdrawals({ status, ...query }),
    queryFn: () => api.withdrawals.adminList(status, query),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['withdrawals'] });
  const approve = useMutation({ mutationFn: (id: string) => api.withdrawals.approve(id), onSuccess: invalidate });
  const reject = useMutation({
    mutationFn: (id: string) => api.withdrawals.reject(id, 'Rejected by admin'),
    onSuccess: invalidate,
  });

  const columns = useMemo(
    () => [
      col.accessor('user_code', { header: 'User ID', cell: (i) => i.getValue() ?? '—' }),
      col.accessor('amount_usd', { header: 'Requested', cell: (i) => formatUsd(i.getValue()) }),
      // Alpha One charges no admin fee, so net always equals the request.
      col.accessor('fee_usd', { header: 'Fee', cell: (i) => formatUsd(i.getValue()) }),
      col.accessor('net_usd', { header: 'Net', cell: (i) => formatUsd(i.getValue()) }),
      col.accessor('amount_lds', { header: 'LDS', cell: (i) => formatLds(i.getValue()) }),
      col.accessor('wallet_address', {
        header: 'Address',
        cell: (i) => `${i.getValue().slice(0, 8)}…${i.getValue().slice(-6)}`,
      }),
      col.accessor('status', {
        header: 'Status',
        cell: (i) => <Chip tone={statusTone(String(i.getValue()))}>{String(i.getValue())}</Chip>,
      }),
      col.accessor('created_at', { header: 'Requested at', cell: (i) => formatDateTime(i.getValue()) }),
      col.display({
        id: 'actions',
        header: 'Actions',
        cell: (info) => {
          const row = info.row.original;
          if (row.status !== 'pending') return '—';
          return (
            <span className="flex gap-1">
              <Chip as="button" tone="success" onClick={() => approve.mutate(row.id)}>
                Approve
              </Chip>
              <Chip as="button" tone="danger" onClick={() => reject.mutate(row.id)}>
                Reject
              </Chip>
            </span>
          );
        },
      }),
    ],
    [approve, reject],
  );

  return (
    <>
      <PageHeader
        title="Withdrawals"
        description="0% admin fee · $25 minimum · 24×7 auto withdrawal"
        tabs={WITHDRAWAL_TABS}
      />
      <DataTable<Withdrawal>
        title={`${status[0].toUpperCase()}${status.slice(1)} Withdrawals`}
        columns={columns}
        data={data?.data}
        meta={data?.meta}
        isLoading={isLoading}
        error={error as Error | null}
        onPageChange={setPage}
      />
    </>
  );
}
